/**
 * 输入栏（任务 10d，两端共用）：textarea autosize + 发送按钮。
 * 键盘/整理口径在 composerModel.ts；发送走 messagesConnection().send（乐观更新
 * 与状态推送由 10a~10c 链路负责，本组件发完即清空不等结果）。
 *
 * - 调用方（ChatPage）按 conversationKey 加 key remount：切会话不串输入内容。
 * - 草稿（14.12）：输入防抖 500ms 存（draftsConnection→Worker kv，空即清）、
 *   卸载时在途即冲（flush）、发送即清；挂载时从 drafts 切片恢复初值（切片晚于
 *   挂载回包时补应用一次，appliedRef 门禁不覆盖已输入内容）。编辑态不碰草稿
 *   （textarea 里是改稿不是草稿；进编辑前的输入已由防抖/暂存链保住）。
 * - 字号 16px（SCSS）：iOS 聚焦不自动放大（PROMPT 硬口径）。
 * - 发送后保持焦点：PC 连续打字；H5 软键盘不塌（点按钮 preventDefault 防失焦）。
 * - 图片（12b）：按钮走隐藏 file input（**刻意不加 capture**——iOS/安卓的系统
 *   选择器自带「拍照 / 相册」两项，capture 会锁死只能拍照）；PC 粘贴剪贴板图片。
 *   发送回调由 ChatPage 注入（与拖拽路径共用提示条，useImageSender）。
 * - 附件（14.3b）：📎 走无 accept 限制的 file input（同样不加 capture），任意
 *   类型交 sendAttachments 分流（图片归图片管线/视频 7/文件 8，fileSend.ts）。
 * - 语音（14.2b-2）：输入框空时麦克风钮占发送钮位（有文本回到发送钮）；移动端
 *   长按录音松开发送、左滑 72px 取消（pointer capture 跟手），桌面点击起止+
 *   显式取消钮。无 MediaRecorder/getUserMedia 隐藏入口（compat.md）。
 * - 编辑（14.7b）：editing 非空 = 编辑态——进入时暂存当前输入（内存级，14.12
 *   草稿接入前刷新即丢与引用态同级）、textarea 置入原文；取消/提交成功（ChatPage
 *   清 editing）恢复暂存草稿；失败 editing 保持，改稿留在输入框可改可重试。
 *   编辑态隐藏图片/附件/麦克风入口（编辑只改文本），发送钮分流 onSubmitEdit。
 * - @提及（14.11b，仅群聊）：输入 '@'（其前是行首/空白/边界标点）弹成员面板
 *   （MentionPanel），选中插入 `@公开名 `；文本里删掉 `@名称`/`@全员` 即撤销
 *   对应目标；发送帧 envelope 带 t/g（全员优先，mentionModel 单点）。入口只留
 *   输入触发——旧版另有输入栏 @ 按钮，新版入口已挤，刻意偏差。
 * - 表情/贴纸（14.16b）：😊 钮开 StickerPanel（MentionPanel 同构双页面板）——
 *   emoji 插入光标处（面板保持打开连点）；贴纸点击即发走通用 send（msgType=2
 *   兼容通道，buildStickerEnvelope；名片 14.4 同先例零 Worker 改动），发帧成功
 *   recordStickerSent（不等回显，14.16a 口径）。贴纸归并图片位 2（群禁图片时
 *   点贴纸报 onMediaBlocked，旧版「禁止发送图片和表情」同口径）；emoji 是纯
 *   文本不受媒体掩码限制。与 @面板互斥（开一个关另一个）。
 * - 群限制态（14.15b，restriction 由 ChatPage 从 timeline.composer 注入）：
 *   muted=整栏禁言——textarea 禁输入+「禁言中」占位（旧版 _buildMutedInputContent
 *   同语义），发送/麦克风/图片/附件钮全隐藏，录音中被禁即取消；blockedMedia=
 *   分位拦截（旧版只拦图片位 2，新版按后端掩码扩到 4/7/8）——入口保留但点击/
 *   粘贴改报 onMediaBlocked（toast 文案在 ChatPage，blockedMediaText 单点），
 *   附件钮仅 7、8 全禁才拦（混投过滤在 ChatPage filterBlockedAttachments）。
 */
import type { JSX } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { draftsConnection, messagesConnection } from '~/store/appWorker';
import { beginInteraction, INTERACTIONS } from '~/perf/interaction';
import { useGlobal } from '~/store/useGlobal';
import { buildTextEnvelope } from '~/chat/preview';
import { buildReplyExt, type ReplyRef } from '~/chat/replyModel';
import { replyThumbPath } from '~/chat/messageModel';
import { resolveMediaUrl } from '~/chat/mediaUrl';
import { MsgType } from '~/chat/msgTypes';
import { formatVoiceDuration } from '~/chat/formatTime';
import {
  addMentionTarget,
  EMPTY_MENTION_STATE,
  MENTION_ALL_TEXT,
  insertMentionText,
  mentionAll,
  mentionEnvelopeFields,
  shouldOpenMentionPanel,
  syncMentionState,
  type MentionState,
} from '~/chat/mentionModel';
import {
  isMediaBlocked,
  UNRESTRICTED,
  type ComposerRestriction,
} from '~/chat/groupRestrictionModel';
import {
  autosizeHeight,
  composerKeyIntent,
  normalizeDraftText,
  pickImageFiles,
} from './composerModel';
import { useVoiceRecorder } from './useVoiceRecorder';
import type { MentionSelection } from './MentionPanel';
import { MentionPanelLazy } from './MentionPanelLazy';
import { StickerPanelLazy } from './StickerPanelLazy';
import {
  IconAt,
  IconClose,
  IconFile,
  IconFlame,
  IconMic,
  IconPersonAdd,
  IconPhotoLibrary,
  IconPlusCircle,
  IconSmile,
} from '~/ui/icons';
import { insertEmojiAt } from './stickerPanelModel';
import { buildStickerEnvelope, type RecentSticker } from '~/chat/stickerModel';
import { stickersConnection } from '~/store/appWorker';
import styles from './composer.module.scss';

/** 编辑目标（14.7b）：会话 key 过滤在 ChatPage，这里只认当前会话的目标 */
export interface ComposerEditDraft {
  readonly msgId: number;
  /** 原文（进入编辑时置入输入框） */
  readonly text: string;
}

/** 草稿保存防抖（14.12）：旧版 GetStorage 同步即存；IndexedDB+RPC 下防抖合并连续击键 */
const DRAFT_SAVE_DEBOUNCE_MS = 500;

export function Composer({
  conversationKey,
  onSendImages,
  onSendAttachments,
  replyTo,
  onCancelReply,
  editing,
  onCancelEdit,
  onSubmitEdit,
  isGroup,
  afterViewSeconds,
  restriction,
  onMediaBlocked,
  onPickContactCard,
  focusRef,
}: {
  /** 会话 key（14.12 草稿存取；调用方按它 remount 本组件） */
  conversationKey: string;
  onSendImages?: (files: File[]) => void;
  onSendAttachments?: (files: File[]) => void;
  /** 打开名片选择器（21d-2）；缺省=不渲染「名片」格 */
  onPickContactCard?: () => void;
  /**
   * 对外暴露「聚焦输入框」（21x）：调用方（ChatPage）点消息区时用它把焦点送回输入框。
   * 用 ref 而不是把 textarea ref 透出去——调用方只该能「请求聚焦」，
   * 不该拿到 DOM 去改别的。
   */
  focusRef?: { current: (() => void) | null };
  /** 引用回复（14.6）：非空时输入栏上方显示预览条，发送帧带 reply ext */
  replyTo?: ReplyRef | null;
  onCancelReply?: () => void;
  /** 消息编辑（14.7b）：非空时进入编辑态（与 replyTo 互斥，ChatPage 保证） */
  editing?: ComposerEditDraft | null;
  onCancelEdit?: () => void;
  /** 编辑提交：受理结果由 ChatPage 处理（成功清 editing，失败 toast 留编辑态） */
  onSubmitEdit?: (text: string) => void;
  /** 群聊（14.11b）：启用 @提及——输入 '@' 弹成员面板、发送帧带 t/g */
  isGroup?: boolean;
  /** 阅后即焚秒数（14.13a；仅私聊>0）：发送文本 envelope 盖 clock */
  afterViewSeconds?: number;
  /** 群限制态（14.15b；缺省不限制 fail-open）：muted 禁言栏 + 媒体分位拦截 */
  restriction?: ComposerRestriction;
  /** 被禁媒体入口点击时回调（msgType ∈ {2,4,7,8}；ChatPage 出 toast） */
  onMediaBlocked?: (msgType: number) => void;
}) {
  const isMobile = useGlobal((s) => s.layout.isMobile);
  // 引用预览条的缩略图（21ah-2）：引用图片时显真图，不止一行「[图片]」
  const cdn = useGlobal((s) => s.config?.cdn ?? '');
  const replyThumb = replyTo == null ? '' : resolveMediaUrl(cdn, replyThumbPath(replyTo));
  // 阅后即焚橙色态（14.13c）：仅私聊且已开启
  const burnActive = isGroup !== true && (afterViewSeconds ?? 0) > 0;
  // 群限制态（14.15b）：muted=整栏禁言；imageBlocked 同时拦粘贴；附件钮仅
  // 视频+文件全禁才拦（部分禁时放进选择器，混投过滤在 ChatPage）
  const r = restriction ?? UNRESTRICTED;
  const muted = r.muted;
  const imageBlocked = isMediaBlocked(r, MsgType.Image);
  const voiceBlocked = isMediaBlocked(r, MsgType.Voice);
  const attachBlocked = isMediaBlocked(r, MsgType.Video) && isMediaBlocked(r, MsgType.File);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const attachRef = useRef<HTMLInputElement | null>(null);
  const [hasText, setHasText] = useState(false);
  const recorder = useVoiceRecorder();
  const recording = recorder.phase !== 'idle';
  // 表情/贴纸面板（14.16b）：开关态；与 @面板互斥（开一个关另一个）
  const [stickerOpen, setStickerOpen] = useState(false);
  // 更多托盘（21d，旧版 _buildMorePanel 同构）：⊕ 开/关，与贴纸、@ 面板三者互斥
  const [moreOpen, setMoreOpen] = useState(false);
  // 录音中被禁言（信令实时推送）：当场取消——cancel 在 idle 态是 no-op，
  // recorder 身份随渲染变也只是幂等空转；贴纸面板同步收起
  const cancelRecording = recorder.cancel;
  useEffect(() => {
    if (muted) {
      cancelRecording();
      setStickerOpen(false);
      setMoreOpen(false);
    }
  }, [muted, cancelRecording]);
  /** 移动端按下点 X：pointermove 相对它算左滑位移 */
  const pressXRef = useRef(0);
  // @提及（14.11b，仅群聊）：状态↔文本同步/插入/t-g 构造全在 mentionModel。
  // 状态走 ref——只在发送时读，输入期不触发重渲染；组件按会话 remount（ChatPage
  // key），切会话自动清。replaceAt=由输入 '@' 触发（插入时替换那个 '@'）。
  const mentionRef = useRef<MentionState>(EMPTY_MENTION_STATE);
  const [mentionOpen, setMentionOpen] = useState<{ replaceAt: boolean } | null>(null);

  // 把聚焦能力挂给调用方（21x）；卸载时摘掉，避免指向已卸载的 textarea
  useEffect(() => {
    if (focusRef === undefined) return;
    focusRef.current = () => taRef.current?.focus();
    return () => {
      focusRef.current = null;
    };
  }, [focusRef]);

  const autosize = useCallback(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = '0';
    el.style.height = `${autosizeHeight(el.scrollHeight)}px`;
  }, []);

  // 草稿（14.12）。lastDraftRef 镜像「应作为草稿的文本」——编辑态的改稿不进来，
  // 卸载冲刷也不依赖届时可能已被摘除的 taRef（卸载时 ref 先清再跑 cleanup）。
  const lastDraftRef = useRef('');
  const draftTimerRef = useRef<number | null>(null);
  const draftPendingRef = useRef(false);
  const flushDraft = useCallback(() => {
    if (draftTimerRef.current !== null) {
      clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
    if (!draftPendingRef.current) return;
    draftPendingRef.current = false;
    draftsConnection().saveDraft(conversationKey, lastDraftRef.current);
  }, [conversationKey]);
  const scheduleDraftSave = useCallback(
    (text: string) => {
      lastDraftRef.current = text;
      draftPendingRef.current = true;
      if (draftTimerRef.current !== null) clearTimeout(draftTimerRef.current);
      draftTimerRef.current = window.setTimeout(flushDraft, DRAFT_SAVE_DEBOUNCE_MS);
    },
    [flushDraft],
  );
  // 卸载冲刷：切会话/退出聊天页时在途草稿当场落库（不足 500ms 的尾巴不丢）
  useEffect(() => flushDraft, [flushDraft]);

  // 恢复初值：切片就绪（null→非 null）后应用一次。通常挂载时已就绪（登录即整批
  // 快照）；冷启动深链时可能晚到，appliedRef + 输入框仍空才写，不覆盖用户输入
  const draftText = useGlobal((s) =>
    s.drafts === null ? null : (s.drafts[conversationKey]?.text ?? ''),
  );
  const draftAppliedRef = useRef(false);
  useEffect(() => {
    const el = taRef.current;
    if (draftAppliedRef.current || draftText === null) return;
    draftAppliedRef.current = true;
    if (draftText === '' || el === null || el.value !== '' || editing != null) return;
    el.value = draftText;
    lastDraftRef.current = draftText;
    setHasText(el.value.trim() !== '');
    autosize();
  }, [draftText, editing, autosize]);

  // 右端按钮归属（21y）：桌面恒麦克风、无发送钮（回车即发）；移动端沿用
  // 「空=麦克风 / 有字=发送」。编辑态两端都给发送钮当确认。见 JSX 处注释。
  const showMic = editing == null && recorder.supported && (!isMobile || !hasText);
  const showSend = !showMic && (isMobile || editing != null);

  const doSend = useCallback(() => {
    const el = taRef.current;
    if (!el || muted) return;
    const text = normalizeDraftText(el.value);
    if (text === '') return;
    if (editing != null) {
      // 编辑提交：不清输入框——成功由 ChatPage 清 editing（下方 effect 恢复
      // 暂存草稿），失败改稿留在输入框可重试
      onSubmitEdit?.(text);
      el.focus();
      return;
    }
    // @提及（14.11b）：按最终文本再同步一次（防漏输入事件），t/g 进 envelope；
    // 全员优先/g 口径在 mentionModel；发送后状态清零
    const mention = isGroup === true
      ? mentionEnvelopeFields(syncMentionState(mentionRef.current, text))
      : undefined;
    // 交互反馈打点（20c）：起点 = 用户点发送/按回车的这一刻；终点在 MessageList
    // （乐观气泡进 items 的那一帧）。输入框清空其实更早（本函数下方同步执行），
    // 但那不是「消息已上屏」的反馈，刻意不拿它当终点。
    beginInteraction(INTERACTIONS.sendMessage);
    void messagesConnection().send({
      msgType: MsgType.Text,
      // 阅后即焚（14.13a）：仅私聊盖 clock（旧版 `clock: isGroup ? 0 : n` 同口径）
      content: buildTextEnvelope(text, mention, isGroup === true ? 0 : (afterViewSeconds ?? 0)),
      // 引用回复（14.6）：ext 走通用发送链原样进帧（旧版 sendText 同口径）
      ...(replyTo != null ? { ext: buildReplyExt(replyTo) } : {}),
    });
    mentionRef.current = EMPTY_MENTION_STATE;
    setMentionOpen(null);
    if (replyTo != null) onCancelReply?.();
    el.value = '';
    setHasText(false);
    // 发送即清草稿（旧版同口径）：立即冲刷，不等防抖
    lastDraftRef.current = '';
    draftPendingRef.current = true;
    flushDraft();
    autosize();
    el.focus();
  }, [autosize, replyTo, onCancelReply, editing, onSubmitEdit, isGroup, afterViewSeconds, flushDraft, muted]);

  // 面板选择：插入 `@名称 `（输入 '@' 触发的替换那个 '@'）并记目标；插入名用
  // 公开名（mentionModel 文件头：消息发给全群，禁好友备注）
  const onMentionSelect = useCallback(
    (target: MentionSelection | 'all') => {
      const el = taRef.current;
      const open = mentionOpen;
      if (!el || open === null) return;
      const name = target === 'all' ? MENTION_ALL_TEXT.slice(1) : target.name;
      const caret = el.selectionStart ?? el.value.length;
      const r = insertMentionText(el.value, caret, name, open.replaceAt);
      el.value = r.text;
      el.setSelectionRange(r.caret, r.caret);
      mentionRef.current =
        target === 'all'
          ? mentionAll()
          : addMentionTarget(mentionRef.current, target.uid, target.name);
      setMentionOpen(null);
      setHasText(el.value.trim() !== '');
      scheduleDraftSave(el.value); // 程序性改文本同样进草稿（面板只在非编辑态出现）
      autosize();
      el.focus();
    },
    [mentionOpen, autosize, scheduleDraftSave],
  );
  const onMentionSelectAll = useCallback(() => onMentionSelect('all'), [onMentionSelect]);
  const closeMentionPanel = useCallback(() => setMentionOpen(null), []);

  // ---- 表情/贴纸面板（14.16b）----
  const closeStickerPanel = useCallback(() => setStickerOpen(false), []);
  // emoji 插入光标处（面板保持打开连点；程序性改文本同 @面板口径：进草稿、保焦点。
  // 面板只在非编辑态渲染，这里不另判 editing）
  const onInsertEmoji = useCallback(
    (emoji: string) => {
      const el = taRef.current;
      if (!el) return;
      const caret = el.selectionStart ?? el.value.length;
      const r = insertEmojiAt(el.value, caret, emoji);
      el.value = r.text;
      el.setSelectionRange(r.caret, r.caret);
      setHasText(el.value.trim() !== '');
      scheduleDraftSave(el.value);
      autosize();
      el.focus();
    },
    [autosize, scheduleDraftSave],
  );
  // 贴纸点击即发（msgType=2 兼容通道，旧版 _sendRemoteStickerMessage 同形）：
  // 群禁图片位 2 归并贴纸（44→2，groupRestrictionModel）；clock 仅私聊盖（文本
  // 同口径）；发帧成功（不等回显）才记最近发送（14.16a recordSent 口径）。
  const onSendSticker = useCallback(
    (sticker: RecentSticker) => {
      if (imageBlocked) {
        onMediaBlocked?.(MsgType.Image);
        return;
      }
      void messagesConnection()
        .send({
          msgType: MsgType.Image,
          content: buildStickerEnvelope(sticker, isGroup === true ? 0 : (afterViewSeconds ?? 0)),
        })
        .then((ok) => {
          if (ok) stickersConnection().recordStickerSent(sticker);
        });
    },
    [imageBlocked, onMediaBlocked, isGroup, afterViewSeconds],
  );

  // 选中「回复」后聚焦输入框（旧版 requestFocus 同口径；H5 顺带弹出软键盘）
  useEffect(() => {
    if (replyTo != null) taRef.current?.focus();
  }, [replyTo]);

  /** 进入编辑时的草稿暂存（null→非空沿才存；换编辑目标不覆盖） */
  const savedDraftRef = useRef('');
  const prevEditRef = useRef<ComposerEditDraft | null>(null);
  useEffect(() => {
    const el = taRef.current;
    const prev = prevEditRef.current;
    prevEditRef.current = editing ?? null;
    if (!el || (editing ?? null) === prev) return;
    if (editing != null) {
      if (prev === null) savedDraftRef.current = el.value;
      el.value = editing.text;
    } else {
      // 取消/提交成功：恢复进入编辑前的草稿
      el.value = savedDraftRef.current;
      lastDraftRef.current = el.value; // 后续冲刷按恢复后的文本（改稿不进草稿）
    }
    setHasText(el.value.trim() !== '');
    autosize();
    el.focus();
  }, [editing, autosize]);

  // 移动=长按录音（pointer capture，松开发送/左滑取消）；桌面=点击起止。
  // mouse 也走 pointer 事件，移动布局下桌面鼠标按住同样可录。
  const micGesture: JSX.DOMAttributes<HTMLButtonElement> = isMobile
    ? {
        onPointerDown: (e) => {
          e.preventDefault(); // 不夺焦点、不触发合成 click/选区
          if (voiceBlocked) {
            // 群禁语音（14.15b）：不启动录音，报拦截（toast 在 ChatPage）
            onMediaBlocked?.(MsgType.Voice);
            return;
          }
          pressXRef.current = e.clientX;
          try {
            e.currentTarget.setPointerCapture(e.pointerId);
          } catch {
            /* 非活动指针（合成事件）无捕获：move/up 仍落在按钮上，继续即可 */
          }
          recorder.start();
        },
        onPointerMove: (e) => recorder.slide(e.clientX - pressXRef.current),
        onPointerUp: () => recorder.stop(),
        onPointerCancel: () => recorder.cancel(),
      }
    : {
        onMouseDown: (e) => e.preventDefault(),
        onClick: () => {
          if (voiceBlocked) {
            onMediaBlocked?.(MsgType.Voice);
            return;
          }
          if (recording) recorder.stop();
          else recorder.start();
        },
      };

  // ⚠️ composer 的直接子节点全部带 key（21d 发现）：它们都是**条件渲染的 div**，
  // 不带 key 时 Preact 按下标配对，面板一开一关会让下标整体平移 → 把输入行那个
  // div 当成面板复用、真输入行被重建。表现是「关掉贴纸面板的同一下，⊕/发送按钮
  // 的 DOM 节点被换掉」，点击在 detached 节点上落空。
  return (
    <div className={styles.composer} data-testid="composer" data-muted={muted ? 'true' : undefined}>
      {isGroup === true && mentionOpen !== null && editing == null && (
        <MentionPanelLazy
          key="mention"
          mobile={isMobile}
          onSelect={onMentionSelect}
          onSelectAll={onMentionSelectAll}
          onClose={closeMentionPanel}
        />
      )}
      {recorder.tip !== '' && (
        <div key="voice-tip" className={styles.voiceTip} data-testid="voice-tip">
          {recorder.tip}
        </div>
      )}
      {editing != null && (
        // 编辑条（14.7b）：replyBar 同款样式，「编辑消息」+ 原文 + ×
        <div key="edit-bar" className={styles.replyBar} data-testid="composer-edit">
          <span className={styles.replyBarBody}>
            <span className={styles.replyBarName}>编辑消息</span>
            <span className={styles.replyBarText}>
              {editing.text !== '' ? editing.text : '(空)'}
            </span>
          </span>
          <button
            type="button"
            className={styles.replyBarClose}
            data-testid="composer-edit-cancel"
            aria-label="取消编辑"
            onMouseDown={(e) => e.preventDefault()} // 不夺焦点（发送按钮同口径）
            onClick={onCancelEdit}
          >
            <IconClose class={styles.closeGlyph} />
          </button>
        </div>
      )}
      {replyTo != null && (
        // 引用预览条（旧版 _buildReplyBar 同构）：竖线 + 「回复 xx」 + 快照 + ×
        <div key="reply-bar" className={styles.replyBar} data-testid="composer-reply">
          {/* 引用图片显缩略图（21ah-2，与气泡内嵌引用块同口径） */}
          {replyThumb !== '' && (
            <img
              className={styles.replyBarThumb}
              src={replyThumb}
              alt=""
              data-testid="composer-reply-thumb"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          <span className={styles.replyBarBody}>
            <span className={styles.replyBarName}>{`回复 ${replyTo.name}`.trim()}</span>
            <span className={styles.replyBarText}>
              {replyTo.text !== '' ? replyTo.text : '(空)'}
            </span>
          </span>
          <button
            type="button"
            className={styles.replyBarClose}
            data-testid="composer-reply-cancel"
            aria-label="取消回复"
            onMouseDown={(e) => e.preventDefault()} // 不夺焦点（发送按钮同口径）
            onClick={onCancelReply}
          >
            <IconClose class={styles.closeGlyph} />
          </button>
        </div>
      )}
      <div key="row" className={styles.composerRow}>
      {/* ⊕ 更多（21d，旧版 chat-input-more-button 同位）：图片/文件/名片收进托盘，
          输入栏只留 ⊕ / @ / ☺ / 发送四个位，与旧版一致 */}
      {!recording && editing == null && !muted && (onSendImages || onSendAttachments || onPickContactCard) && (
        <button
          type="button"
          className={styles.moreButton}
          data-testid="composer-more"
          data-active={moreOpen ? 'true' : undefined}
          aria-label="更多"
          onMouseDown={(e) => e.preventDefault()} // 同发送按钮：不夺焦点
          onClick={() => {
            setMentionOpen(null);
            setStickerOpen(false);
            setMoreOpen((o) => !o);
          }}
        >
          <IconPlusCircle class={styles.icon} />
        </button>
      )}
      {/* 隐藏 file input 常驻（不随托盘开合挂卸）：粘贴发图与 e2e 的
          setInputFiles 都直接打到它们身上，挂卸会把这两条路打断 */}
      {onSendImages && (
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          data-testid="composer-image-input"
          onChange={(e) => {
            const files = pickImageFiles(e.currentTarget.files);
            e.currentTarget.value = ''; // 允许连选同一张（change 按值去重）
            if (files.length > 0) onSendImages(files);
          }}
        />
      )}
      {onSendAttachments && (
        <input
          ref={attachRef}
          type="file"
          multiple
          hidden
          data-testid="composer-attach-input"
          onChange={(e) => {
            const files = Array.from(e.currentTarget.files ?? []);
            e.currentTarget.value = ''; // 允许连选同一个文件（change 按值去重）
            if (files.length > 0) onSendAttachments(files);
          }}
        />
      )}
      {/* 阅后即焚橙色态（14.13c；旧版输入壳橙底橙框+火焰图标同构）：仅私聊
          开启时；编辑态不盖 clock 不渲染 */}
      {burnActive && !recording && editing == null && (
        <span className={styles.burnFlame} data-testid="composer-burn" aria-hidden="true">
          <IconFlame class={styles.burnFlameIcon} />
        </span>
      )}
      {recording ? (
        // 录音态占 textarea 位（仅输入框为空时可进入录音，无草稿可丢）
        <div
          className={styles.recordingBar}
          data-testid="voice-recording"
          data-cancel-armed={recorder.cancelArmed ? 'true' : undefined}
        >
          <span className={styles.recordDot} aria-hidden="true" />
          <span className={styles.recordClock} data-testid="voice-clock">
            {formatVoiceDuration(recorder.elapsedMs)}
          </span>
          <span className={styles.recordHint}>
            {isMobile ? (recorder.cancelArmed ? '松开取消' : '松开发送 · 左滑取消') : '正在录音…'}
          </span>
          {!isMobile && (
            <button
              type="button"
              className={styles.recordCancel}
              data-testid="voice-cancel"
              onClick={() => recorder.cancel()}
            >
              取消
            </button>
          )}
        </div>
      ) : (
        <textarea
          ref={taRef}
          className={
            burnActive && editing == null ? `${styles.input} ${styles.inputBurn}` : styles.input
          }
          data-testid="composer-input"
          rows={1}
          // 点输入框收起面板/托盘（旧版 GestureDetector onTap 同口径）。桌面上
          // 输入行抬到了点外关闭层之上（见 composer.module.scss），这条就是
          // 「点回输入框就关面板」的唯一来源
          onPointerDown={() => {
            setStickerOpen(false);
            setMoreOpen(false);
          }}
          disabled={muted}
          placeholder={muted ? '禁言中' : '输入消息'}
          enterkeyhint="send"
          onInput={(e) => {
            const el = e.currentTarget;
            setHasText(el.value.trim() !== '');
            autosize();
            // 草稿（14.12）：编辑态的改稿不进草稿（进编辑前的输入已保住）
            if (editing == null) scheduleDraftSave(el.value);
            if (isGroup === true && editing == null) {
              // 文本删掉 `@名称`/`@全员` 即撤销对应目标（旧版 _syncMentionStateWithText）
              mentionRef.current = syncMentionState(mentionRef.current, el.value);
              if (shouldOpenMentionPanel(el.value, el.selectionStart ?? el.value.length)) {
                setMentionOpen({ replaceAt: true });
                setStickerOpen(false); // 两面板互斥（14.16b）
              }
            }
          }}
          onKeyDown={(e) => {
            if (composerKeyIntent(e) === 'send') {
              e.preventDefault();
              doSend();
            } else if (e.key === 'Escape' && mentionOpen !== null) {
              // @面板优先：Esc 先关面板（面板自身的 window 监听兜底，幂等）
              e.preventDefault();
              setMentionOpen(null);
            } else if (e.key === 'Escape' && stickerOpen) {
              // 贴纸面板次之（面板自身的 window 监听兜底，幂等）
              e.preventDefault();
              setStickerOpen(false);
            } else if (e.key === 'Escape' && editing != null) {
              // PC 惯例：Esc 退出编辑态（恢复暂存草稿，上方 effect）
              e.preventDefault();
              onCancelEdit?.();
            } else if (e.key === 'Escape' && replyTo != null) {
              // PC 惯例：Esc 关闭引用预览条（PROMPT「Esc 关闭弹层」口径）
              e.preventDefault();
              onCancelReply?.();
            }
          }}
          onPaste={(e) => {
            if (!onSendImages) return;
            const files = pickImageFiles(e.clipboardData?.files ?? null);
            if (files.length > 0) {
              e.preventDefault(); // 拦下图片粘贴（部分浏览器会同时粘入文件名文本）
              if (imageBlocked) onMediaBlocked?.(MsgType.Image);
              else onSendImages(files);
            }
          }}
        />
      )}
      {/* @成员（21d，旧版 chat-input-mention-button）：仅群聊，点开 @面板
          （与输入 '@' 触发同一个面板，replaceAt=false：不替换任何字符） */}
      {isGroup === true && !recording && editing == null && !muted && (
        <button
          type="button"
          className={styles.iconButton}
          data-testid="composer-mention"
          data-active={mentionOpen !== null ? 'true' : undefined}
          aria-label="选择 @ 成员"
          onMouseDown={(e) => e.preventDefault()} // 同发送按钮：不夺焦点
          onClick={() => {
            setStickerOpen(false);
            setMoreOpen(false);
            setMentionOpen((o) => (o === null ? { replaceAt: false } : null));
          }}
        >
          <IconAt class={styles.icon} />
        </button>
      )}
      {!recording && editing == null && !muted && (
        <button
          type="button"
          className={styles.iconButton}
          data-testid="composer-emoji"
          data-active={stickerOpen ? 'true' : undefined}
          aria-label="表情与贴纸"
          onMouseDown={(e) => e.preventDefault()} // 同发送按钮：不夺焦点（emoji 插入点跟随光标）
          onClick={() => {
            setMentionOpen(null); // 面板互斥（14.16b；21d 起托盘也进这组）
            setMoreOpen(false);
            setStickerOpen((o) => {
              // 移动端开面板即收键盘（21ah-3）：面板走文档流占输入行下方那块，
              // 键盘再叠上来会把消息列表挤没。caret 位置在 textarea 上留着，
              // 失焦后 emoji 照样插在原处。
              if (!o && isMobile) taRef.current?.blur();
              return !o;
            });
          }}
        >
          <IconSmile class={styles.icon} />
        </button>
      )}
      {/*
        右端按钮（21y，人工：「这里应该有个语音按钮的 去哪里了 而且不许发送按钮 回车就发送」）：
        - **桌面**：恒显麦克风，**没有发送按钮**——回车即发（composerKeyIntent 本就如此）。
          此前有文本就把麦克风换成「发送」，于是想发语音得先清空输入框，确实别扭。
        - **移动端仍保留发送按钮**：那边 Enter 是换行（软键盘换行键），
          没有发送按钮就**根本发不出去**。这不是偷懒，是 H5 的硬约束。
        - 编辑态两端都给按钮：编辑提交要有个明确的确认动作，回车虽然也能提交，
          但「改完没有任何可点的确认」会让人以为没生效。
      */}
      {muted ? null : showMic ? (
        // 桌面录音中按钮变「发送」收口点击起止（移动端是长按，不需要）
        <button
          type="button"
          className={!isMobile && recording ? styles.sendButton : styles.micButton}
          data-testid="composer-mic"
          data-recording={recording ? 'true' : undefined}
          data-blocked={voiceBlocked ? 'true' : undefined}
          aria-label={recording ? '停止并发送' : '录制语音'}
          {...micGesture}
        >
          {!isMobile && recording ? '发送' : <IconMic class={styles.icon} />}
        </button>
      ) : showSend ? (
        <button
          type="button"
          className={styles.sendButton}
          data-testid="composer-send"
          disabled={!hasText}
          // mousedown/touch 的 preventDefault 由 onMouseDown 做：点按钮不夺走
          // textarea 焦点，H5 软键盘保持展开
          onMouseDown={(e) => e.preventDefault()}
          onClick={doSend}
        >
          发送
        </button>
      ) : null}
      </div>
      {/* 表情/贴纸面板（14.16b）渲染在输入行**下方**（21ah-3，人工指图「表情选择
          界面挡住了输入框要往上提」）：移动端它是走文档流的面板区（更多托盘同位
          同策），输入行留在它上方可见；桌面仍是 absolute 浮在输入栏上方，DOM 位置
          不影响。 */}
      {stickerOpen && editing == null && !muted && !recording && (
        <StickerPanelLazy
          key="sticker"
          mobile={isMobile}
          onInsertEmoji={onInsertEmoji}
          onSendSticker={onSendSticker}
          onClose={closeStickerPanel}
        />
      )}
      {/* 更多托盘（21d，旧版 _buildMorePanel 同构，连配色一起对齐）：
          贴在输入行下方（旧版面板区就在输入行与屏底之间），不走贴纸面板那套
          「桌面浮在上方／移动端底部 sheet」——托盘只有三格，浮层反而更重 */}
      {moreOpen && !recording && editing == null && !muted && (
        <div key="more-tray" className={styles.moreTray} data-testid="composer-more-tray">
          {onSendImages && (
            <button
              type="button"
              className={styles.trayTile}
              data-testid="composer-image"
              data-blocked={imageBlocked ? 'true' : undefined}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                // 群禁图片（14.15b，贴纸 44 同位）：不开选择器，报拦截
                if (imageBlocked) onMediaBlocked?.(MsgType.Image);
                else fileRef.current?.click();
              }}
            >
              <span className={styles.trayIcon} style={{ color: '#54b8f3' }}>
                <IconPhotoLibrary class={styles.trayGlyph} />
              </span>
              相册
            </button>
          )}
          {onSendAttachments && (
            <button
              type="button"
              className={styles.trayTile}
              data-testid="composer-attach"
              data-blocked={attachBlocked ? 'true' : undefined}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                // 视频+文件全禁才整钮拦（部分禁时混投过滤在 ChatPage）
                if (attachBlocked) onMediaBlocked?.(MsgType.File);
                else attachRef.current?.click();
              }}
            >
              <span className={styles.trayIcon} style={{ color: '#ffcd6a' }}>
                <IconFile class={styles.trayGlyph} />
              </span>
              文件
            </button>
          )}
          {onPickContactCard && (
            <button
              type="button"
              className={styles.trayTile}
              data-testid="composer-card"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setMoreOpen(false); // 浮层接管，托盘先收
                onPickContactCard();
              }}
            >
              <span className={styles.trayIcon} style={{ color: '#d669ed' }}>
                <IconPersonAdd class={styles.trayGlyph} />
              </span>
              名片
            </button>
          )}
        </div>
      )}
    </div>
  );
}
